import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiClient } from "../../App";

const INITIAL_FORM_DATA = {
  title: "",
  contract_type: "",
  contract_value: "",
  effective_date: "",
  expiration_date: "",
  department: "",
  spend_category: "",
  jurisdiction: "",
  supplier_unique_id: "",
  contract_owner_name: "",
  contract_owner_email: "",
  contract_scope: "",
  content: "",
  contracting_party: {
    legal_name: "",
    registered_address: "",
    contact_email: "",
    contact_phone: "",
  },
  contract_unique_id: "",
  sections: [],
  additional_clauses: [],
  deliverables: [],
  kpis: [],
  template_metadata: null,
};

export function useContractEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  // Core state
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [clauses, setClauses] = useState([]);

  // UI toggles
  const [templateSheetOpen, setTemplateSheetOpen] = useState(false);
  const [clauseSheetOpen, setClauseSheetOpen] = useState(false);
  const [draftPreviewOpen, setDraftPreviewOpen] = useState(false);
  const [editorExpanded, setEditorExpanded] = useState(false);
  const [contextualReviewOpen, setContextualReviewOpen] = useState(false);
  const [placeholdersApplied, setPlaceholdersApplied] = useState(false);

  // AI state
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [aiExtractText, setAiExtractText] = useState("");
  const [aiExtracting, setAiExtracting] = useState(false);
  const [aiResults, setAiResults] = useState(null);

  // Computed
  const hasUnfilledPlaceholders = useMemo(() => {
    const allText = [
      formData.content || "",
      ...(formData.sections || []).map((s) => s.content || ""),
    ].join("\n");
    return /\[[^\[\]]{2,60}\]/g.test(allText);
  }, [formData.content, formData.sections]);

  const contentVisible = placeholdersApplied || !hasUnfilledPlaceholders;

  // ── Data Fetching ──

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [templatesRes, clausesRes] = await Promise.all([
          apiClient.get("/templates"),
          apiClient.get("/clauses"),
        ]);
        setTemplates(templatesRes.data);
        setClauses(clausesRes.data);
      } catch (e) {
        /* Templates/clauses data fetch is non-critical */
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isEditing) return;
    const fetchContract = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get(`/contracts/${id}`);
        const c = res.data;
        setFormData({
          title: c.title || "",
          contract_type: c.contract_type || "",
          contract_value: c.contract_value || "",
          currency: c.currency || "",
          effective_date: c.effective_date || "",
          expiration_date: c.expiration_date || "",
          department: c.department || "",
          spend_category: c.spend_category || "",
          jurisdiction: c.jurisdiction || "",
          supplier_unique_id: c.supplier_unique_id || "",
          contract_owner_name: c.custom_fields?.contract_owner_name || "",
          contract_owner_email: c.custom_fields?.contract_owner_email || "",
          contract_scope: c.custom_fields?.contract_scope || "",
          content: c.content || "",
          contracting_party: c.contracting_party || INITIAL_FORM_DATA.contracting_party,
          contract_unique_id: c.custom_fields?.contract_unique_id || "",
          sections: c.sections || [],
          additional_clauses: c.additional_clauses || [],
          deliverables: c.custom_fields?.deliverables || [],
          kpis: c.custom_fields?.kpis || [],
          template_metadata: c.custom_fields?.template_metadata || null,
        });
        if (c.custom_fields?.template_id) {
          const tmpl = templates.find((t) => t.id === c.custom_fields.template_id);
          if (tmpl) setSelectedTemplate(tmpl);
        }
      } catch {
        toast.error("Failed to load contract");
        navigate("/contracts");
      } finally {
        setLoading(false);
      }
    };
    fetchContract();
  }, [id, isEditing, navigate, templates]);

  // ── Form Handlers ──

  const handleChange = useCallback((field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({ ...prev, [parent]: { ...prev[parent], [child]: value } }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  }, []);

  const replacePlaceholders = useCallback((text) => {
    if (!text) return text;
    return text
      .replace(/\[CLIENT_NAME\]/g, formData.contract_owner_name || "[Client Name]")
      .replace(/\[SUPPLIER_NAME\]/g, formData.contracting_party.legal_name || "[Supplier Name]")
      .replace(/\[EFFECTIVE_DATE\]/g, formData.effective_date || "[Date]")
      .replace(/\[EXPIRATION_DATE\]/g, formData.expiration_date || "[Date]");
  }, [formData.contract_owner_name, formData.contracting_party.legal_name, formData.effective_date, formData.expiration_date]);

  const handleApplyTemplate = useCallback((template) => {
    setSelectedTemplate(template);
    const sections = (template.sections || []).map((section) => ({
      ...section,
      id: `section-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      templateSectionId: section.id,
      content: replacePlaceholders(section.content),
    }));
    setFormData((prev) => ({
      ...prev,
      contract_type: template.category,
      sections,
      content: template.content || "",
      template_metadata: template.template_metadata || null,
      jurisdiction: template.governing_law && !prev.jurisdiction ? template.governing_law : prev.jurisdiction,
    }));
    toast.success(`Applied template: ${template.name}`);
  }, [replacePlaceholders]);

  const handleUpdateSection = useCallback((sectionId, content) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => (s.id === sectionId ? { ...s, content } : s)),
    }));
  }, []);

  const handleAddClause = useCallback((clause) => {
    if (formData.additional_clauses.some((c) => c.clause_id === clause.id)) {
      toast.info("Clause already added");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      additional_clauses: [
        ...prev.additional_clauses,
        { id: `clause-${Date.now()}`, clause_id: clause.id, name: clause.name, category: clause.category, content: clause.content },
      ],
    }));
    toast.success(`Added clause: ${clause.name}`);
  }, [formData.additional_clauses]);

  // ── Save ──

  const handleSave = useCallback(async () => {
    if (!formData.title.trim()) { toast.error("Contract title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: formData.title, contract_type: formData.contract_type,
        contract_value: formData.contract_value, currency: formData.currency,
        effective_date: formData.effective_date, expiration_date: formData.expiration_date,
        department: formData.department, spend_category: formData.spend_category,
        jurisdiction: formData.jurisdiction, supplier_unique_id: formData.supplier_unique_id,
        content: formData.content, contracting_party: formData.contracting_party,
        sections: formData.sections, additional_clauses: formData.additional_clauses,
        custom_fields: {
          contract_owner_name: formData.contract_owner_name,
          contract_owner_email: formData.contract_owner_email,
          contract_scope: formData.contract_scope,
          template_id: selectedTemplate?.id,
          deliverables: formData.deliverables, kpis: formData.kpis,
          template_metadata: formData.template_metadata,
        },
      };
      if (isEditing) {
        await apiClient.put(`/contracts/${id}`, payload);
        toast.success("Contract updated successfully");
      } else {
        const res = await apiClient.post("/contracts", payload);
        toast.success("Contract created successfully");
        navigate(`/contracts/${res.data.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save contract");
    } finally {
      setSaving(false);
    }
  }, [formData, selectedTemplate, isEditing, id, navigate]);

  // ── AI Handlers ──

  const handleAiDraft = useCallback(async (sectionId) => {
    if (!aiPrompt.trim()) return;
    setAiGenerating(true);
    try {
      const res = await apiClient.post("/ai/draft", {
        prompt: aiPrompt, section_id: sectionId,
        context: formData.content || formData.sections.map((s) => s.content).join("\n"),
        contract_type: formData.contract_type, jurisdiction: formData.jurisdiction,
      });
      const taskId = res.data.task_id;
      const poll = setInterval(async () => {
        try {
          const p = await apiClient.get(`/ai/task/${taskId}`);
          if (p.data.status === "completed" && p.data.result) {
            clearInterval(poll);
            const content = p.data.result.generated_content || "";
            if (sectionId) { handleUpdateSection(sectionId, content); }
            else { setFormData((prev) => ({ ...prev, content: prev.content + "\n\n" + content })); }
            setAiPrompt("");
            setAiGenerating(false);
            toast.success("AI draft generated");
          } else if (p.data.status === "failed") {
            clearInterval(poll);
            setAiGenerating(false);
            toast.error(p.data.error || "AI drafting failed");
          }
        } catch { clearInterval(poll); setAiGenerating(false); toast.error("Lost connection to AI task"); }
      }, 4000);
    } catch { toast.error("AI drafting failed"); setAiGenerating(false); }
  }, [aiPrompt, formData.content, formData.sections, formData.contract_type, formData.jurisdiction, handleUpdateSection]);

  const handleAiExtract = useCallback(async () => {
    if (!aiExtractText.trim()) return;
    setAiExtracting(true);
    try {
      const res = await apiClient.post("/ai/extract-deliverables-kpis", {
        text: aiExtractText, contract_type: formData.contract_type,
      });
      setAiResults(res.data);
    } catch { toast.error("AI extraction failed"); } finally { setAiExtracting(false); }
  }, [aiExtractText, formData.contract_type]);

  const handleApplySuggestion = useCallback((sectionName, text, isNew = false) => {
    if (isNew) {
      const newSection = {
        id: `section-${Date.now()}`, type: "custom", name: sectionName,
        content: text, is_editable: true, allow_override: false,
        order: formData.sections.length,
      };
      setFormData((prev) => ({ ...prev, sections: [...prev.sections, newSection] }));
      toast.success(`Added new clause: ${sectionName}`);
    } else {
      const idx = formData.sections.findIndex((s) =>
        s.name.toLowerCase().includes(sectionName.toLowerCase()) || sectionName.toLowerCase().includes(s.name.toLowerCase())
      );
      if (idx >= 0) {
        const updated = [...formData.sections];
        updated[idx] = { ...updated[idx], content: text };
        setFormData((prev) => ({ ...prev, sections: updated }));
        toast.success(`Updated: ${formData.sections[idx].name}`);
      } else {
        handleApplySuggestion(sectionName, text, true);
      }
    }
  }, [formData.sections]);

  return {
    // Identifiers
    id, isEditing, navigate,
    // Form
    formData, setFormData, handleChange,
    // State
    loading, saving,
    // Templates & Clauses
    templates, selectedTemplate, clauses,
    templateSheetOpen, setTemplateSheetOpen,
    clauseSheetOpen, setClauseSheetOpen,
    handleApplyTemplate, handleAddClause,
    // Editor UI
    editorExpanded, setEditorExpanded,
    draftPreviewOpen, setDraftPreviewOpen,
    contextualReviewOpen, setContextualReviewOpen,
    placeholdersApplied, setPlaceholdersApplied,
    contentVisible,
    // Save
    handleSave,
    // Sections
    handleUpdateSection, setActiveSection, activeSection,
    // AI
    aiPrompt, setAiPrompt, aiGenerating, handleAiDraft,
    aiExtractText, setAiExtractText, aiExtracting, aiResults, setAiResults, handleAiExtract,
    handleApplySuggestion,
  };
}
